import React from "react";
import {
  TextField,
  MenuItem,
  Select,
  OutlinedInput,
  FormHelperText,
  FormControl,
  Chip,
  Box,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { FixedSizeList } from "react-window";
import { getPointerEventsStyles, getReadOnlyStyles } from "../../lib/styles";

const ITEM_HEIGHT = 48;
const MAX_ITEM_COUNT = 8;

function renderSelectedValues(selected, lookupOptions, handleRemove, readOnly) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {selected.map((val) => {
        const label = lookupOptions.find((opt) => opt.id === val)?.label || val;
        return (
          <Chip
            key={val}
            label={label}
            onDelete={!readOnly ? () => handleRemove(val) : undefined}
            deleteIcon={<CancelIcon onMouseDown={(e) => e.stopPropagation()} />}
            sx={{
              height: "100%",
              borderRadius: "12px",
              ".MuiChip-label": { whiteSpace: "normal" },
            }}
          />
        );
      })}
    </Box>
  );
}

function VirtualizedMenuItem({ style, option, value, onChange }) {
  const isSelected = Array.isArray(value) && value.includes(option.id);
  const handleToggle = () => {
    const newValue = isSelected
      ? value.filter((v) => v !== option.id)
      : [...(value ?? []), option.id];
    onChange(newValue);
  };

  return (
    <MenuItem
      style={{
        ...style,
        whiteSpace: "normal",
        wordWrap: "break-word",
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
      }}
      selected={isSelected}
      onClick={handleToggle}
    >
      {option.label}
    </MenuItem>
  );
}

export function SelectInput({
  fieldConfig,
  controllerField,
  fieldState,
  lookupOptions,
}) {
  const { type, readOnly } = fieldConfig;
  const { value, onChange, ...rest } = controllerField;
  const isMulti = type === "selectMulti";
  const useVirtualized = isMulti && lookupOptions.length >= 50;

  const handleRemove = (valToRemove) => {
    const newValue = value.filter((v) => v !== valToRemove);
    onChange(newValue);
  };

  if (!isMulti) {
    return (
      <TextField
        {...rest}
        select
        disabled={readOnly}
        value={value ?? ""}
        onChange={onChange}
        variant="outlined"
        size="small"
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        fullWidth
        sx={getReadOnlyStyles(readOnly)}
        InputProps={{ sx: getPointerEventsStyles(readOnly) }}
      >
        {lookupOptions.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            sx={{
              whiteSpace: "normal",
              borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <FormControl
      variant="outlined"
      size="small"
      fullWidth
      error={!!fieldState.error}
      sx={getReadOnlyStyles(readOnly)}
    >
      <Select
        {...rest}
        multiple
        disabled={readOnly}
        value={value ?? []}
        onChange={onChange}
        input={<OutlinedInput />}
        renderValue={(selected) =>
          renderSelectedValues(selected, lookupOptions, handleRemove, readOnly)
        }
      >
        {useVirtualized ? (
          <FixedSizeList
            height={Math.min(
              ITEM_HEIGHT * MAX_ITEM_COUNT,
              ITEM_HEIGHT * lookupOptions.length
            )}
            itemCount={lookupOptions.length}
            itemSize={ITEM_HEIGHT}
            width="100%"
          >
            {({ index, style }) => (
              <VirtualizedMenuItem
                style={style}
                option={lookupOptions[index]}
                value={value}
                onChange={onChange}
              />
            )}
          </FixedSizeList>
        ) : (
          lookupOptions.map((option) => (
            <MenuItem
              key={option.id}
              value={option.id}
              sx={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
      {!!fieldState.error && (
        <FormHelperText>{fieldState.error.message}</FormHelperText>
      )}
    </FormControl>
  );
}
